import { Component } from "react";
import { Link } from "react-router-dom";
import { AlertOctagon, RefreshCw, Home } from "lucide-react";
import { Button } from "@/components/ui/button";

// Iter 115 — App-wide crash boundary.
//
// One malformed API response or a runtime bug used to whitewash the
// entire SPA. This component catches any render-time error, shows a
// friendly fallback (with the error hash for support), and offers
// Retry + Home as the two escape hatches.
//
// Design intent:
//   * Boundaries can be nested. Wrap AppShell for the "last-line-of-
//     defence" fallback, and wrap individual heavy pages (Triage,
//     BookkeepingQuality) to keep the sidebar functional when one
//     tab misbehaves.
//   * `resetKey` (route pathname) auto-resets the boundary on
//     navigation — a stuck fallback shouldn't outlive its route.
//   * Every caught error also fires `window.dispatchEvent("close:app-error")`
//     so a future Sentry init (N-11) can pick it up without another
//     boundary rewrite.

export default class ErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { error: null, errorId: null };
  }

  static getDerivedStateFromError(error) {
    // Short hash makes it easy for a user to quote in a bug report.
    const hash = Math.abs(
      (error?.message || "unknown")
        .split("")
        .reduce((h, c) => (h * 31 + c.charCodeAt(0)) | 0, 0)
    ).toString(36).slice(0, 6).toUpperCase();
    return { error, errorId: hash };
  }

  componentDidCatch(error, info) {
    // Keep the console signal loud for local dev.
    console.error("[ErrorBoundary] caught:", error, info);
    try {
      window.dispatchEvent(new CustomEvent("close:app-error", {
        detail: { error, info, scope: this.props.scope || "app" },
      }));
    } catch (_) { /* ignore */ }
  }

  componentDidUpdate(prev) {
    // Auto-reset when the caller-provided resetKey flips (e.g. route change).
    if (this.state.error && prev.resetKey !== this.props.resetKey) {
      this.setState({ error: null, errorId: null });
    }
  }

  reset = () => this.setState({ error: null, errorId: null });

  render() {
    if (!this.state.error) return this.props.children;
    const { fallback, scope = "app" } = this.props;
    if (fallback) return fallback({ error: this.state.error, errorId: this.state.errorId, reset: this.reset });
    return (
      <div className="min-h-[60vh] flex items-center justify-center p-6" data-testid={`error-boundary-${scope}`}>
        <div className="max-w-md w-full border border-red-200 bg-red-50 rounded-md p-6 space-y-4">
          <div className="flex items-start gap-3">
            <AlertOctagon className="h-5 w-5 text-red-600 shrink-0 mt-0.5"/>
            <div className="space-y-1">
              <h2 className="font-display tracking-tight text-lg text-red-900">Something broke on this page.</h2>
              <p className="text-sm text-red-800/90">
                We hit an unexpected error. The rest of Close is still working —
                head home or retry this view.
              </p>
              <p className="text-[11px] text-red-700 font-mono pt-2">
                Ref&nbsp;<span data-testid={`error-boundary-ref-${scope}`}>{this.state.errorId}</span>
              </p>
            </div>
          </div>
          <div className="flex gap-2">
            <Button onClick={this.reset} data-testid={`error-boundary-retry-${scope}`} size="sm" variant="outline" className="gap-1.5">
              <RefreshCw className="h-3 w-3"/> Retry
            </Button>
            <Button asChild size="sm" className="gap-1.5 bg-slate-900 hover:bg-slate-800 text-white">
              <Link to="/" data-testid={`error-boundary-home-${scope}`}>
                <Home className="h-3 w-3"/> Home
              </Link>
            </Button>
          </div>
        </div>
      </div>
    );
  }
}
